import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext({});

type AuthProviderProps = {
  children: JSX.Element;
};

type Contact = {
  contactId: string;
  contact: string;
  contactType: string;
  name?: string;
  isContactValidated: boolean;
  siteId: string;
  contactIdSf?: string;
  isNeedUpdate: boolean;
};

type Question = {
  id: string;
  question: string;
};

type User = {
  id: string;
  isActive: boolean;
  isGuideNavigationTour: boolean;
  creationDate: string;
  updatePasswordDate: string;
  failedLoginAttempts: number;
};

type AccessToken = {
  accessToken: string;
};

type SiteId = {
  siteId: string;
};

type CustomerCode = {
  customerCode: string;
};

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [userData, setUserData] = useState(() => {
    const userName = localStorage.getItem("@UltragazEmpresarial:userName");
    const loginEmail = localStorage.getItem("@UltragazEmpresarial:loginEmail");

    const customerType = localStorage.getItem(
      "@UltragazEmpresarial:customerType"
    );
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const customerName = localStorage.getItem(
      "@UltragazEmpresarial:customerName"
    );
    const customerAddressLine = localStorage.getItem(
      "@UltragazEmpresarial:customerAddressLine"
    );
    const userCustomerRelationshipId = localStorage.getItem(
      "@UltragazEmpresarial:userCustomerRelationshipId"
    );
    const customerId = localStorage.getItem("@UltragazEmpresarial:customerId");
    const customerSiteCode = localStorage.getItem(
      "@UltragazEmpresarial:customerSiteCode"
    );
    const customerCode = localStorage.getItem(
      "@UltragazEmpresarial:customerCode"
    );
    const customerSiteId = localStorage.getItem(
      "@UltragazEmpresarial:customerSiteId"
    );
    const accessToken = localStorage.getItem(
      "@UltragazEmpresarial:accessToken"
    );
    const refreshToken = localStorage.getItem(
      "@UltragazEmpresarial:refreshToken"
    );

    if (accessToken && refreshToken) {
      return {
        userName,
        loginEmail,
        customerType,
        document,
        customerName,
        customerAddressLine,
        userCustomerRelationshipId,
        customerId,
        customerSiteCode,
        customerCode,
        customerSiteId,
        accessToken,
        refreshToken,
      };
    }
    return {};
  });

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<AccessToken | "">("");
  const [siteId, setSiteId] = useState<SiteId | "">("");
  const [customerCode, setCustomerCode] = useState<CustomerCode | "">("");

  const updateContacts = useCallback((newContacts: Contact[]) => {
    setContacts(newContacts);
  }, []);

  const updateQuestions = useCallback((newQuestions: Question[]) => {
    setQuestions(newQuestions);
  }, []);

  const updateUser = useCallback((newUser: User) => {
    setUser(newUser);
  }, []);

  const getAccessToken = useCallback((accessToken: AccessToken) => {
    setAccessToken(accessToken);
  }, []);

  const getSiteId = useCallback((siteId: SiteId) => {
    setSiteId(siteId);
  }, []);

  const getCustomerCode = useCallback((customerCode: CustomerCode) => {
    setCustomerCode(customerCode);
  }, []);

  const signIn = useCallback(
    async ({ loginEmail, accessToken, refreshToken, customerType }) => {
      localStorage.removeItem("@UltragazEmpresarial:customerSiteCode");
      localStorage.removeItem("@UltragazEmpresarial:customerSiteId");
      localStorage.removeItem("@UltragazEmpresarial:customerCode");
      localStorage.removeItem("@UltragazEmpresarial:customerId");
      localStorage.removeItem("@UltragazEmpresarial:customerName");
      localStorage.removeItem(
        "@UltragazEmpresarial:userCustomerRelationshipId"
      );
      localStorage.removeItem("@UltragazEmpresarial:userName");
      localStorage.removeItem("@UltragazEmpresarial:customerType");
      localStorage.removeItem("@UltragazEmpresarial:userId");

      localStorage.setItem("@UltragazEmpresarial:loginEmail", loginEmail);
      localStorage.setItem("@UltragazEmpresarial:accessToken", accessToken);
      localStorage.setItem("@UltragazEmpresarial:refreshToken", refreshToken);
      localStorage.setItem("@UltragazEmpresarial:customerType", customerType);

      setUserData({
        ...userData,
        loginEmail,
        accessToken,
        refreshToken,
        customerType,
      });
    },
    [setUserData, userData]
  );

  const signOut = useCallback(async () => {
    localStorage.clear();
    setUserData({});
    setContacts([]);
    setQuestions([]);
    setUser(null);
    setAccessToken("");
    setSiteId("");
    setCustomerCode("");
    window.location.href = "/";
  }, [setUserData]);

  const setUserInfos = useCallback(
    async (infos) => {
      const userValues = { ...userData, ...infos };
      setUserData(userValues);

      localStorage.setItem(
        "@UltragazEmpresarial:city",
        userValues.city || localStorage.getItem("@UltragazEmpresarial:city")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:stateCode",
        userValues.stateCode ||
          localStorage.getItem("@UltragazEmpresarial:stateCode")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerName",
        userValues.customerName ||
          localStorage.getItem("@UltragazEmpresarial:customerName")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:userName",
        userValues.userName ||
          localStorage.getItem("@UltragazEmpresarial:userName")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:document",
        userValues.document ||
          localStorage.getItem("@UltragazEmpresarial:document")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerName",
        userValues.customerName ||
          localStorage.getItem("@UltragazEmpresarial:customerName")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerAddressLine",
        userValues.customerAddressLine ||
          localStorage.getItem("@UltragazEmpresarial:customerAddressLine")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:userCustomerRelationshipId",
        userValues.userCustomerRelationshipId ||
          localStorage.getItem(
            "@UltragazEmpresarial:userCustomerRelationshipId"
          )
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerId",
        userValues.customerId ||
          localStorage.getItem("@UltragazEmpresarial:customerId")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerSiteCode",
        userValues.customerSiteCode ||
          localStorage.getItem("@UltragazEmpresarial:customerSiteCode")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerCode",
        userValues.customerCode ||
          localStorage.getItem("@UltragazEmpresarial:customerCode")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:customerSiteId",
        userValues.customerSiteId ||
          localStorage.getItem("@UltragazEmpresarial:customerSiteId")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:commercialRegion",
        userValues.commercialRegion ||
          localStorage.getItem("@UltragazEmpresarial:commercialRegion")
      );
      localStorage.setItem(
        "@UltragazEmpresarial:userId",
        userValues.userId || localStorage.getItem("@UltragazEmpresarial:userId")
      );
    },
    [setUserData, userData]
  );

  const value = useMemo(() => {
    return {
      userData,
      signIn,
      signOut,
      setUserInfos,
      contacts,
      questions,
      user,
      accessToken,
      siteId,
      customerCode,
      updateContacts,
      updateQuestions,
      updateUser,
      getAccessToken,
      getSiteId,
      getCustomerCode,
    };
  }, [
    userData,
    contacts,
    questions,
    user,
    accessToken,
    customerCode,
    getSiteId,
    signIn,
    signOut,
    setUserInfos,
    updateContacts,
    updateQuestions,
    updateUser,
    getAccessToken,
    getSiteId,
    getCustomerCode,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): any => {
  return useContext(AuthContext);
};
